import { Injectable } from '@angular/core';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { LoginService } from '../login/login.service';
import { Router } from '@angular/router';
import { PriceTagInfo } from 'projects/shared/src/public-api';
import { environment } from 'projects/shared/src/environments/environment';
import { ApiService } from '../api-service/api.service';

@Injectable({
  providedIn: 'root',
})
export class PriceTagService {
  constructor(
    private router: Router,
    private apiService: ApiService,
    private loginService: LoginService
  ) {}

  /**
   * Retrieves a price tag based on the provided tag information.
   * If the access token is expired, attempts to refresh the token before making the API call.
   *
   * @param tagInfo - The information used to retrieve the price tag.
   * @returns Observable containing the API response or an error.
   */
  getPriceTag(tagInfo: PriceTagInfo): Observable<any> {
    const params = new HttpParams({
      fromObject: tagInfo as any,
    });

    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    // Get access and refresh tokens
    const accessToken = this.loginService.getActiveToken();
    const refreshToken = this.loginService.getRefreshToken();

    // If no tokens are available, redirect to login
    if (!accessToken || !refreshToken) {
      console.error('Error with tokens in getPriceTag');
      this.loginService.clearTokens();
      this.router.navigate(['/login']);
      return of(null);
    }

    // Check if the access token has expired
    const isTokenExpired = this.loginService.isTokenExpired(accessToken);

    // If the access token is expired, refresh it
    if (isTokenExpired) {
      return this.loginService.refreshAccessToken(refreshToken).pipe(
        switchMap(() => {
          // Get the refreshed access token
          const refreshedAccessToken = this.loginService.getActiveToken();
          headers = headers.set(
            'Authorization',
            `Bearer ${refreshedAccessToken}`
          );

          // Make the API call with the refreshed token
          return this.apiService.makeApiCall<any>(
            environment.apiGetPriceTag,
            headers,
            'GET',
            params
          );
        }),
        catchError((error) => {
          // Handle token refresh failure and redirect to login if needed
          console.error('Error refreshing token', error);
          this.loginService.clearTokens();
          this.router.navigate(['/login']);
          return throwError(() => error);
        })
      );
    }

    // If the access token is valid, make the API call directly
    headers = headers.set('Authorization', `Bearer ${accessToken}`);
    return this.apiService.makeApiCall<any>(
      environment.apiGetPriceTag,
      headers,
      'GET',
      params
    );
  }
}
